// tenant-config.service.ts

import { Injectable, computed, inject, signal } from '@angular/core';
import {
    FeatureConfig,
    FeatureKey,
    RouteAccessKey,
    TableConfig,
    TableKey,
    TenantApiOperationConfig,
    TenantConfig
} from './tenant-config.model';
import { LOCAL_TENANT_CONFIG } from './tenant-config.local';
import { resolveExperienceTenantId } from './tenant-id.resolver';
import {
    DEFAULT_EXPERIENCE_RUBRIC,
    resolveExperienceRubric,
} from '../core/experience/experience-params.util';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

export interface TenantConfigBundleResponse {
    contentVersion: string;
    appId: string;
    tenantId: string;
    rubric?: string;
    config: TenantConfig;
}

@Injectable({
    providedIn: 'root'
})
export class TenantConfigService {
    private readonly http = inject(HttpClient);
    private readonly _config = signal<TenantConfig | null>(null);

    readonly config = computed(() => this._config());

    async load(): Promise<void> {
        if (environment.experienceTenantConfigEnabled) {
            try {
                const tenantId = this.resolveTenantId();
                const rubric = this.resolveRubric();
                const response = await firstValueFrom(
                    this.http.get<TenantConfigBundleResponse>(
                        `${environment.experienceApiBaseUrl}/tenant-config/bundle`,
                        {
                            params: {
                                appId: environment.experienceAppId,
                                tenantId,
                                rubric
                            }
                        }
                    )
                );

                if (response?.config) {
                    this._config.set(response.config);
                    return;
                }
            } catch (error) {
                console.warn('[TenantConfig] Remote load failed, using local fallback', error);
            }
        }
        this._config.set(LOCAL_TENANT_CONFIG);
    }

    private resolveTenantId(): string {
        try {
            const raw = localStorage.getItem('tenant');
            if (raw) {
                const tenant = JSON.parse(raw) as { code?: string; id?: number };
                return resolveExperienceTenantId(
                    tenant,
                    environment.experienceTenantId ?? 'default',
                );
            }
        } catch {
            // ignore malformed tenant cache
        }
        return environment.experienceAppId ?? 'default';
    }

    /** Same resolution as theme/i18n: tenant.experienceRubric → user.rubric → DEFAULT. */
    private resolveRubric(): string {
        try {
            const tenantRaw = localStorage.getItem('tenant');
            const userRaw = localStorage.getItem('user');
            const tenant = tenantRaw
                ? (JSON.parse(tenantRaw) as { experienceRubric?: string } | null)
                : null;
            const user = userRaw
                ? (JSON.parse(userRaw) as { rubric?: string } | null)
                : null;
            return resolveExperienceRubric(tenant?.experienceRubric, user?.rubric);
        } catch {
            return DEFAULT_EXPERIENCE_RUBRIC;
        }
    }

    get snapshot(): TenantConfig {
        const config = this._config();

        if (!config) {
            throw new Error('Tenant config not loaded');
        }

        return config;
    }

    getFeatureConfig(
        featureKey: FeatureKey,
        fallback: FeatureConfig = FeatureConfig.ENABLED
    ): FeatureConfig {
        return this.snapshot.features[featureKey] ?? fallback;
    }

    isFeatureEnabled(featureKey: FeatureKey): boolean {
        return this.getFeatureConfig(featureKey) === FeatureConfig.ENABLED;
    }

    isFeatureDisabled(featureKey: FeatureKey): boolean {
        return this.getFeatureConfig(featureKey) === FeatureConfig.DISABLED;
    }

    isFeatureHidden(featureKey: FeatureKey): boolean {
        return this.getFeatureConfig(featureKey) === FeatureConfig.HIDDEN;
    }

    isFeatureVisible(featureKey: FeatureKey): boolean {
        return this.getFeatureConfig(featureKey) !== FeatureConfig.HIDDEN;
    }

    hasFeature(featureKey: FeatureKey): boolean {
        return this.isFeatureEnabled(featureKey);
    }

    shouldRenderFeature(featureKey: FeatureKey): boolean {
        return this.isFeatureVisible(featureKey);
    }

    canUseFeature(featureKey: FeatureKey): boolean {
        return this.isFeatureEnabled(featureKey);
    }

    canNavigate(featureKey: FeatureKey): boolean {
        return this.isFeatureEnabled(featureKey);
    }

    getRouteConfig(
        routeKey: RouteAccessKey,
        fallback: FeatureConfig = FeatureConfig.ENABLED
    ): FeatureConfig {
        return this.snapshot.routes?.[routeKey] ?? fallback;
    }

    canAccessRoute(routeKey: RouteAccessKey): boolean {
        return this.getRouteConfig(routeKey) === FeatureConfig.ENABLED;
    }

    isRouteDenied(routeKey: RouteAccessKey): boolean {
        return !this.canAccessRoute(routeKey);
    }

    getApiDefaults(): Record<string, string | number | boolean> {
        return this.snapshot.api?.defaults ?? {};
    }

    getApiOperation(operation: string): TenantApiOperationConfig | undefined {
        return this.snapshot.api?.operations?.[operation];
    }

    getDefaultRoute(): RouteAccessKey | null {
        return this.snapshot.defaultRoute ?? null;
    }

    getDefaultRouteFallbacks(): RouteAccessKey[] {
        return this.snapshot.defaultRouteFallbacks ?? [];
    }

    getDefaultRouteCandidates(): RouteAccessKey[] {
        const defaultRoute = this.getDefaultRoute();
        const fallbacks = this.getDefaultRouteFallbacks();

        if (!defaultRoute) {
            return fallbacks;
        }

        return [
            defaultRoute,
            ...fallbacks.filter(route => route !== defaultRoute)
        ];
    }

    getFirstAccessibleDefaultRoute(): RouteAccessKey | null {
        return this.getDefaultRouteCandidates().find(route =>
            this.canAccessRoute(route)
        ) ?? null;
    }

    getTableConfig(tableKey: TableKey): TableConfig {
        return this.snapshot.tables?.[tableKey] ?? {};
    }
}
